/**
 * /venues/:venueSlug — a venue's public face.
 *
 * Renders inside AppShell (routes.jsx): sidebar, topbar, content column.
 * Art-free, same as the platform landing — type and space carry it
 * (V2-DESIGN-REFERENCE.md §2.6).
 *
 * Three calls, in two waves. by-slug first, because it is what turns the URL's
 * slug into the numeric id the other two are keyed on; those two then go out
 * together rather than in sequence.
 *
 * Everything below the hero is optional and degrades to nothing: a venue with
 * no active season, no open tournament and no settings still renders a
 * coherent page rather than a column of empty headings.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiGet } from '../lib/api.js';
import { formatDateTime, formatAvg } from '../lib/format.js';

/** The soonest tournament still open, which is not the list's first row —
 *  the API orders date DESC for the admin lists that page through history. */
function nextOpen(tournaments) {
  return [...tournaments]
    .filter((t) => t.date)
    .sort((a, b) => a.date.localeCompare(b.date))[0]
    ?? tournaments[0]
    ?? null;
}

export default function VenueLanding() {
  const { venueSlug } = useParams();

  const [state, setState] = useState({ status: 'loading' });
  const [next, setNext]   = useState(null);
  const [table, setTable] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    setNext(null);
    setTable(null);

    apiGet(`/api/venues/by-slug/${venueSlug}`).then(async ({ data, error }) => {
      if (cancelled) return;

      if (error || !data?.venue) {
        setState({ status: 'error', error: error ?? 'Venue not found' });
        return;
      }

      setState({ status: 'ready', venue: data.venue, settings: data.settings ?? {} });

      const id = data.venue.id;
      const [tournaments, standings] = await Promise.all([
        apiGet(`/api/venues/${id}/tournaments?status=registration`),
        apiGet(`/api/venues/${id}/public-standings?limit=5`),
      ]);

      if (cancelled) return;

      // Both are enhancements. A failure on either leaves its section absent
      // rather than turning a working page into an error.
      setNext(nextOpen(tournaments.data?.tournaments ?? []));
      if (standings.data?.season) setTable(standings.data);
    });

    return () => { cancelled = true; };
  }, [venueSlug]);

  if (state.status === 'loading') {
    return <div className="hd-empty">Loading…</div>;
  }

  if (state.status === 'error') {
    return (
      <div className="hd-stack">
        <h1 className="hd-page-title">Venue not found</h1>
        <p className="hd-muted">
          No venue at <code>/{venueSlug}</code>.{' '}
          <Link to="/">Back to the home page</Link>
        </p>
      </div>
    );
  }

  const { venue, settings } = state;
  const charityOn = settings.charity_enabled === '1';

  return (
    <div className="hd-venue">
      {/* ---- Hero ------------------------------------------------------- */}
      <section className="hd-venue-hero">
        <h1 className="hd-venue-name">{venue.name}</h1>

        {settings.venue_tagline && (
          <p className="hd-venue-tagline">{settings.venue_tagline}</p>
        )}

        {settings.address && (
          <p className="hd-venue-address">{settings.address}</p>
        )}

        {!venue.active && (
          <p className="hd-badge hd-badge-neutral" style={{ marginTop: 12 }}>
            Not currently active
          </p>
        )}
      </section>

      {/* ---- Next tournament -------------------------------------------- */}
      <section className="hd-venue-next">
        <div className="hd-tile-label">Next tournament</div>

        {next ? (
          <>
            <div className="hd-venue-next-date">
              {formatDateTime(next.date, next.start_time) ?? 'Date to be confirmed'}
            </div>

            <div className="hd-venue-next-meta">
              {next.night_number != null && <>Night {next.night_number} · </>}
              {next.entry_count ?? 0} of {next.max_entries} spots taken
              {next.entry_fee > 0 && <> · £{Number(next.entry_fee).toFixed(2)} entry</>}
            </div>

            <div className="hd-row" style={{ marginTop: 16 }}>
              <Link
                className="hd-btn hd-btn-primary"
                to={`/venues/${venue.slug}/tournaments/${next.id}/register`}
              >
                Register
              </Link>
            </div>
          </>
        ) : (
          <p className="hd-muted" style={{ marginTop: 4 }}>
            No upcoming tournaments. Check back — new nights open for
            registration here.
          </p>
        )}
      </section>

      {/* ---- Standings preview ------------------------------------------ */}
      {table && table.standings.length > 0 && (
        <section>
          <h2 className="hd-section-title">Current standings</h2>
          <p className="hd-meta" style={{ marginTop: -8, marginBottom: 12 }}>
            {table.season.name}
            {table.playerCount > table.standings.length && (
              <> · top {table.standings.length} of {table.playerCount}</>
            )}
          </p>

          <table className="hd-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                <th>Player</th>
                <th className="hd-table-num">P</th>
                <th className="hd-table-num">Pts</th>
                <th className="hd-table-num">Avg</th>
              </tr>
            </thead>
            <tbody>
              {table.standings.map((row) => (
                <tr key={`${row.position}-${row.displayName}`}>
                  <td className="hd-muted">{row.position}</td>
                  <td>{row.displayName ?? 'Unknown player'}</td>
                  <td className="hd-table-num">{row.tournamentsPlayed}</td>
                  <td className="hd-table-num">{row.totalPoints}</td>
                  <td className="hd-table-num">{formatAvg(row.seasonAvg)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p style={{ marginTop: 12 }}>
            <Link to={`/venues/${venue.slug}/standings`}>View full standings</Link>
          </p>
        </section>
      )}

      {/* ---- Quick links ------------------------------------------------- */}
      <section className="hd-row hd-venue-links">
        <Link className="hd-btn hd-btn-quiet" to={`/venues/${venue.slug}/standings`}>
          Standings
        </Link>
        <Link className="hd-btn hd-btn-quiet" to={`/venues/${venue.slug}/tournaments`}>
          Tournaments
        </Link>
        <Link className="hd-btn hd-btn-quiet" to={`/venues/${venue.slug}/hall-of-fame`}>
          Hall of fame
        </Link>
      </section>

      {/* ---- Charity — only when switched on, and never the centrepiece --- */}
      {charityOn && settings.charity_name && (
        <section className="hd-venue-charity">
          <span className="hd-badge hd-badge-accent">Playing for charity</span>
          <span className="hd-venue-charity-name">{settings.charity_name}</span>
        </section>
      )}

      {/* ---- Footer ------------------------------------------------------ */}
      {(settings.contact_email || settings.contact_phone || settings.map_link) && (
        <footer className="hd-venue-foot">
          {settings.contact_email && (
            <a href={`mailto:${settings.contact_email}`}>{settings.contact_email}</a>
          )}
          {settings.contact_phone && (
            <a href={`tel:${settings.contact_phone.replace(/\s+/g, '')}`}>
              {settings.contact_phone}
            </a>
          )}
          {settings.map_link && (
            <a href={settings.map_link} target="_blank" rel="noopener noreferrer">
              Find us
            </a>
          )}
        </footer>
      )}
    </div>
  );
}
